import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageHeader,
  DashboardCard,
  FilterBar,
  StatusBadge,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@zafroi/ui-kit';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import { useCustomerNameMap } from '@/hooks/useKosh';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { subscriptionStatusVariants } from '@/config/subscription-status';
import { shortDate } from '@/lib/format';

const cap = (s: string) => s.charAt(0) + s.slice(1).toLowerCase();

export function SubscriptionsPage() {
  const navigate = useNavigate();
  const q = useSubscriptions();
  const nameMap = useCustomerNameMap();
  const [search, setSearch] = useState('');

  const subs = q.data ?? [];
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return subs;
    return subs.filter((x) => (nameMap.get(x.customerId) ?? '').toLowerCase().includes(s));
  }, [subs, search, nameMap]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Commercial"
        title="Subscriptions"
        description="Recurring billing schedules — activate, pause, and run them."
      />
      <DashboardCard flushBody>
        <div className="border-b border-border p-3">
          <FilterBar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by customer…"
          />
        </div>
        <QueryStateGuard
          data={filtered}
          isLoading={q.isLoading}
          isError={q.isError}
          emptyTitle="No subscriptions yet"
          emptyDescription="Sell a SaaS-subscription offering to create one."
        >
          {(rowsData) => (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Frequency</TableHead>
                  <TableHead>Next run</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rowsData.map((x) => (
                  <TableRow
                    key={x.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/subscriptions/${x.id}`)}
                  >
                    <TableCell className="font-medium">
                      {nameMap.get(x.customerId) ?? x.customerId.slice(0, 8)}
                    </TableCell>
                    <TableCell>
                      <span className={
                        'rounded px-1.5 py-0.5 text-[11px] font-medium ' +
                        (x.platformManaged ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground')
                      }>
                        {x.platformManaged ? 'ZAFROI' : 'Kosh'}
                      </span>
                    </TableCell>
                    <TableCell>{cap(x.frequency)}</TableCell>
                    <TableCell className="text-muted-foreground">{shortDate(x.nextRunDate)}</TableCell>
                    <TableCell>
                      <StatusBadge status={x.status} variants={subscriptionStatusVariants} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">{shortDate(x.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </QueryStateGuard>
      </DashboardCard>
    </div>
  );
}
